// Re-export all hooks from individual stores
export * from "./sessionStore";
export * from "./companyUserStore";
export * from "./companyStore";
export * from "./permissionsStore";
export * from "./entitlementsStore";

// Import coordinator and initialize it
import { storeCoordinator } from "./coordinator";
import { Session } from "@supabase/supabase-js";

// Import hooks for convenience functions
import {
    useIsAuthenticated,
    useIsLoading as useSessionIsLoading,
    useSession,
} from "./sessionStore";
import { useCompanyUser, useCompanyUserLoading } from "./companyUserStore";
import {
    useCompanyId,
    useCompanyLoading,
    useHasCompanyId,
} from "./companyStore";
import { usePermissionsLoading } from "./permissionsStore";
import { useEntitlementsLoading } from "./entitlementsStore";

// Initialize the coordinator
storeCoordinator.initialize();

// Re-export coordinator functions for backward compatibility
export const setSession = (session: Session | null) => {
    storeCoordinator.setSession(session);
};

export const setLoading = (loading: boolean) => {
    storeCoordinator.setLoading(loading);
};

// Override hooks with coordinator versions for combined state
export const useIsLoading = (): boolean => {
    const sessionLoading = useSessionIsLoading();
    const companyUserLoading = useCompanyUserLoading();
    const permissionsLoading = usePermissionsLoading();
    const entitlementsLoading = useEntitlementsLoading();

    return (
        sessionLoading ||
        companyUserLoading ||
        permissionsLoading ||
        entitlementsLoading
    );
};

export const useIsFullyAuthenticated = () => {
    const session = useSession();
    const companyUser = useCompanyUser();

    return session !== null && companyUser !== null;
};

// Additional convenience hooks for backward compatibility
export const useAuthData = () => {
    const session = useSession();
    const companyUser = useCompanyUser();
    const companyId = useCompanyId();
    const isAuthenticated = useIsAuthenticated();
    const isFullyAuthenticated = useIsFullyAuthenticated();
    const hasCompanyId = useHasCompanyId();

    return {
        session,
        companyUser,
        companyId,
        isAuthenticated,
        isFullyAuthenticated,
        hasCompanyId,
    };
};

export const useLoadingStates = () => {
    const isLoading = useSessionIsLoading();
    const isCompanyUserLoading = useCompanyUserLoading();
    const isCompanyLoading = useCompanyLoading();
    const isPermissionsLoading = usePermissionsLoading();
    const isEntitlementsLoading = useEntitlementsLoading();

    return {
        isLoading,
        isCompanyUserLoading,
        isCompanyLoading,
        isPermissionsLoading,
        isEntitlementsLoading,
    };
};
